/**
 * "Offene Forderungen – aktuell".
 *
 * THIS CARD IS NOT ABOUT THE SELECTED MONTH, AND IT NEVER NAMES ONE.
 * `current_receivables` is everything open RIGHT NOW across all rent months.
 * The Bexio mirror keeps only the current state — `total_remaining` is
 * overwritten on every sync and nothing is versioned — so "what was open on
 * 31.08.2026?" has no answer in this data. Narrowing this block to the selected
 * month would hide exactly the receivables an operator most needs to see: the
 * unpaid invoice from three months ago. The selected period is deliberately not
 * even passed into this component, so it cannot be printed here by accident.
 *
 * ALL SEVEN BUCKETS ARE SHOWN SEPARATELY.
 * `due_today` is its own class because an invoice due today has an age of zero
 * days, which would make a "1–30 Tage" label false. `no_due_date` is its own
 * class because those are open receivables whose age is UNKNOWN — folding them
 * into "Nicht fällig" would assert something the data does not support. A
 * consumer can merge buckets for display; it could never split them back out.
 *
 * The three chips partition `open_current` exactly, which is why
 * `no_due_date_current` cannot hide inside `not_due_current`.
 */
import React from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { formatDateOnly } from "../../../utils/datetime";
import {
  AGING_BUCKET_LABEL,
  AGING_BUCKET_ORDER,
  compactAxisTick,
  fmtMoney,
  SERIES_COLOR,
  toChartNumber,
} from "./revenueAnalyticsFormat";

/** Overdue classes get the warning tint; the two "no claim yet" ones do not. */
const BUCKET_COLOR = {
  not_due: SERIES_COLOR.invoiced,
  due_today: SERIES_COLOR.open,
  overdue_1_30: SERIES_COLOR.open,
  overdue_31_60: SERIES_COLOR.open,
  overdue_61_90: SERIES_COLOR.open,
  overdue_90_plus: "#dc2626",
  no_due_date: "#94a3b8",
};

function Chip({ label, value }) {
  return (
    <div className="rounded-lg border border-border bg-muted/40 px-2.5 py-1.5">
      <div className="text-[11px] text-muted-foreground">{label}</div>
      <div className="text-sm font-semibold text-foreground">{fmtMoney(value)}</div>
    </div>
  );
}

function BucketTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-lg border border-border bg-card px-3 py-2 text-xs shadow-md">
      <div className="font-semibold text-foreground">{p.label}</div>
      <div className="text-muted-foreground">
        {fmtMoney(p.raw)} · {p.count} {p.count === 1 ? "Rechnung" : "Rechnungen"}
      </div>
    </div>
  );
}

export default function RevenueReceivablesCard({ receivables }) {
  const buckets = Array.isArray(receivables?.buckets) ? receivables.buckets : [];
  const byKey = {};
  buckets.forEach((b) => {
    byKey[b.key] = b;
  });

  // Fixed order, every backend key present — a bucket the response omits shows
  // as zero because the contract guarantees the seven partition `open_current`,
  // not because a value was invented for it.
  const data = AGING_BUCKET_ORDER.map((key) => ({
    key,
    label: AGING_BUCKET_LABEL[key] || key,
    raw: byKey[key]?.amount ?? "0",
    amount: toChartNumber(byKey[key]?.amount),
    count: Number(byKey[key]?.invoice_count ?? 0),
  }));

  const openCount = Number(receivables?.open_invoice_count ?? 0);
  const hasOpen = toChartNumber(receivables?.open_current) > 0 || openCount > 0;

  return (
    <div className="flex h-full flex-col rounded-xl border border-border bg-card p-4">
      <h2 className="text-sm font-semibold text-foreground">Offene Forderungen – aktuell</h2>
      <p className="mt-0.5 text-xs text-muted-foreground">
        Gesamter Forderungsbestand über alle Mietmonate · Stand{" "}
        {receivables?.as_of_date ? formatDateOnly(receivables.as_of_date) : "—"}
      </p>

      {!hasOpen ? (
        <p className="mt-6 text-sm text-muted-foreground">
          Keine offenen Forderungen zu diesem Stichtag.
        </p>
      ) : (
        <>
          <div className="mt-3 flex flex-wrap items-baseline gap-2">
            <span className="text-2xl font-bold text-foreground">
              {fmtMoney(receivables?.open_current)}
            </span>
            <span className="text-xs text-muted-foreground">
              aus {openCount} {openCount === 1 ? "Rechnung" : "Rechnungen"}
            </span>
          </div>

          <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
            <Chip label="Fällig" value={receivables?.due_current} />
            <Chip label="Nicht fällig" value={receivables?.not_due_current} />
            <Chip label="Ohne Fälligkeitsdatum" value={receivables?.no_due_date_current} />
          </div>

          <div className="mt-3 h-[190px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: -12 }}>
                <CartesianGrid
                  stroke="currentColor"
                  className="text-border"
                  strokeDasharray="3 4"
                  vertical={false}
                />
                <XAxis
                  dataKey="label"
                  tickLine={false}
                  axisLine={false}
                  interval={0}
                  tick={{ fontSize: 9, fill: "currentColor" }}
                  className="text-muted-foreground"
                />
                <YAxis
                  tickFormatter={compactAxisTick}
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: 11, fill: "currentColor" }}
                  className="text-muted-foreground"
                />
                <Tooltip content={<BucketTooltip />} cursor={false} />
                <Bar dataKey="amount" radius={[4, 4, 0, 0]} isAnimationActive={false}>
                  {data.map((d) => (
                    <Cell key={d.key} fill={BUCKET_COLOR[d.key] || SERIES_COLOR.open} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <table className="sr-only">
            <caption>Offene Forderungen nach Fälligkeitsklasse</caption>
            <thead>
              <tr>
                <th scope="col">Klasse</th>
                <th scope="col">Betrag</th>
                <th scope="col">Rechnungen</th>
              </tr>
            </thead>
            <tbody>
              {data.map((d) => (
                <tr key={d.key}>
                  <th scope="row">{d.label}</th>
                  <td>{fmtMoney(d.raw)}</td>
                  <td>{d.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
